import { useState, useEffect } from 'react';

export const AWARD_METALS_CONFIG_KEY = 'peoplevue_award_metals_config';
export const AWARD_METALS_CONFIG_CHANGE_EVENT = 'peoplevue_award_metals_config_change';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Set by syncAwardMetalsConfig so a later saveAwardMetalsConfig (the admin's
// own color edit) can push straight through without the caller having to
// thread a token.
let apiToken = null;

// The exact shipped values from src/index.css (`:root` for light, `html.dark`
// for dark) — copied verbatim, not re-derived. These are the fallback when no
// override exists, or a field is missing from a stored override.
export const DEFAULT_METAL_COLORS = {
  light: {
    bronze:   { face: '#F7E7DA', ring: '#C98A5E', ink: '#7C4A24', deep: '#8A5227' },
    silver:   { face: '#E7EBEF', ring: '#AFBBC6', ink: '#3C4A56', deep: '#7E8B98' },
    gold:     { face: '#FCF1D5', ring: '#D9AE45', ink: '#6E5211', deep: '#A9782A' },
    onyx:     { face: '#47443F', ring: '#6B655B', ink: '#F0EDE7', deep: '#211F1C', sheen: 'rgba(255,255,255,.20)' },
    platinum: { face: '#F5F7FA', ring: '#8E9BAC', ink: '#2E3A47', deep: '#5E6B7B' },
    obsidian: { face: '#332D45', ring: '#655D7A', ink: '#F5F2FC', deep: '#1C1828', sheen: 'rgba(255,255,255,.24)' },
    diamond:  { face: '#EAF6FF', ring: '#7DD3FC', ink: '#0C4A6E', deep: '#38BDF8' },
  },
  dark: {
    bronze:   { face: '#33241A', ring: '#A56C42', ink: '#E9BE97', deep: '#7A4E2C' },
    silver:   { face: '#292D32', ring: '#8B98A6', ink: '#D8DFE7', deep: '#66727F' },
    gold:     { face: '#3A2F14', ring: '#C79E3C', ink: '#F5DC97', deep: '#9C7530' },
    onyx:     { face: '#3A3733', ring: '#7B746A', ink: '#E9E4DA', deep: '#161512', sheen: 'rgba(255,255,255,.10)' },
    platinum: { face: '#2D323A', ring: '#A9B7C6', ink: '#E4EBF3', deep: '#75838F' },
    obsidian: { face: '#17151E', ring: '#7E7595', ink: '#EFECF8', deep: '#08070C', sheen: 'rgba(255,255,255,.09)' },
    diamond:  { face: '#0C2A3D', ring: '#5EC8F5', ink: '#D6F3FF', deep: '#2FA8DE' },
  },
};

/**
 * Deep-merges a partial/stored override over DEFAULT_METAL_COLORS. This
 * config is two levels deep (theme -> metal -> field), so a shallow spread
 * would blow away untouched metals/fields whenever only one field of one
 * metal (e.g. bronze.ring) was ever saved. Only plain objects are recursed
 * into; anything else (or missing) falls back to the default leaf value.
 */
function deepMergeMetals(defaults, overrides) {
  if (!overrides || typeof overrides !== 'object') return defaults;
  const result = {};
  for (const theme of Object.keys(defaults)) {
    const themeOverride = overrides[theme];
    result[theme] = {};
    for (const metal of Object.keys(defaults[theme])) {
      const metalOverride = themeOverride && typeof themeOverride === 'object' ? themeOverride[metal] : undefined;
      result[theme][metal] = {
        ...defaults[theme][metal],
        ...(metalOverride && typeof metalOverride === 'object' ? metalOverride : {}),
      };
    }
  }
  return result;
}

export function loadAwardMetalsConfig() {
  try {
    const stored = localStorage.getItem(AWARD_METALS_CONFIG_KEY);
    return stored ? deepMergeMetals(DEFAULT_METAL_COLORS, JSON.parse(stored)) : { ...DEFAULT_METAL_COLORS };
  } catch {
    return { ...DEFAULT_METAL_COLORS };
  }
}

function writeLocal(config) {
  try {
    localStorage.setItem(AWARD_METALS_CONFIG_KEY, JSON.stringify(config));
    window.dispatchEvent(new Event(AWARD_METALS_CONFIG_CHANGE_EVENT));
  } catch {
    // ignore
  }
}

/**
 * Hydrate the local cache from the server. Call once after sign-in, same as
 * syncAppConfig and the other tenant-wide settings. Non-fatal on failure —
 * falls back to whatever's already cached locally.
 */
export async function syncAwardMetalsConfig(token) {
  if (!API_BASE || !token) return false;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/award-metals-config`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Award metals config API ${res.status}`);
    writeLocal(deepMergeMetals(DEFAULT_METAL_COLORS, await res.json()));
    return true;
  } catch (err) {
    console.warn('Failed to sync award metals config (non-fatal):', err.message);
    return false;
  }
}

/**
 * Save the tenant-wide award metal colors. Local-first like every other
 * admin setting: the local cache updates (and takes effect) immediately, and
 * the server push is admin-gated server-side and non-fatal so an unreachable
 * endpoint never blocks the edit from working on the admin's own device.
 * Once the push lands, every other user's next syncAwardMetalsConfig() picks
 * it up.
 */
export function saveAwardMetalsConfig(config) {
  writeLocal(config);

  if (API_BASE && apiToken) {
    fetch(`${API_BASE}/award-metals-config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
      body: JSON.stringify(config),
    })
      .then((res) => { if (!res.ok) console.warn(`Award metals config push failed: ${res.status}`); })
      .catch((err) => console.warn('Award metals config push failed:', err.message));
  }
}

export function useAwardMetalsConfig() {
  const [config, setConfig] = useState(loadAwardMetalsConfig);

  useEffect(() => {
    const handle = () => setConfig(loadAwardMetalsConfig());
    window.addEventListener(AWARD_METALS_CONFIG_CHANGE_EVENT, handle);
    return () => window.removeEventListener(AWARD_METALS_CONFIG_CHANGE_EVENT, handle);
  }, []);

  return config;
}
